/** Shared presentation languages; these values never grant account permissions. */
export const SITE_LOCALES = [
  { code: 'en', label: 'English', prefix: 'en', dir: 'ltr' },
  { code: 'zh-Hant', label: '繁體中文（香港）', prefix: 'zh-hant', dir: 'ltr' },
  { code: 'zh-TW', label: '繁體中文（台灣）', prefix: 'zh-tw', dir: 'ltr' },
  { code: 'zh-Hans', label: '简体中文', prefix: 'zh-hans', dir: 'ltr' },
  { code: 'ja', label: '日本語', prefix: 'ja', dir: 'ltr' },
  { code: 'ko', label: '한국어', prefix: 'ko', dir: 'ltr' },
  { code: 'de', label: 'Deutsch', prefix: 'de', dir: 'ltr' },
  { code: 'fr', label: 'Français', prefix: 'fr', dir: 'ltr' },
  { code: 'es', label: 'Español', prefix: 'es', dir: 'ltr' },
  { code: 'pt-BR', label: 'Português (Brasil)', prefix: 'pt-br', dir: 'ltr' },
  { code: 'ar', label: 'العربية', prefix: 'ar', dir: 'rtl' },
  { code: 'he', label: 'עברית', prefix: 'he', dir: 'rtl' },
  { code: 'nl', label: 'Nederlands', prefix: 'nl', dir: 'ltr' },
  { code: 'hi', label: 'हिन्दी', prefix: 'hi', dir: 'ltr' },
  { code: 'id', label: 'Bahasa Indonesia', prefix: 'id', dir: 'ltr' },
  { code: 'nb', label: 'Norsk bokmål', prefix: 'nb', dir: 'ltr' },
  { code: 'sv', label: 'Svenska', prefix: 'sv', dir: 'ltr' },
  { code: 'hu', label: 'Magyar', prefix: 'hu', dir: 'ltr' },
] as const

export type SiteLocale = (typeof SITE_LOCALES)[number]['code']

/** Exact stored codes, preserving the distinctions between the Chinese variants. */
export function siteLocale(value: unknown): SiteLocale | undefined {
  return SITE_LOCALES.find((locale) => locale.code === value)?.code
}

/** BCP 47 language negotiation, shared with the public website. */
export function matchSiteLocale(value: string | null | undefined): SiteLocale | undefined {
  if (!value) return undefined
  try {
    const tag = new Intl.Locale(value)
    if (tag.language === 'zh') {
      if (tag.script === 'Hans') return 'zh-Hans'
      if (tag.region === 'TW') return 'zh-TW'
      if (tag.script === 'Hant' || ['HK', 'MO'].includes(tag.region ?? '')) return 'zh-Hant'
      if (['CN', 'SG'].includes(tag.region ?? '')) return 'zh-Hans'
      return undefined
    }
    if (tag.language === 'pt') return 'pt-BR'
    if (tag.language === 'no') return 'nb'
    return siteLocale(tag.language)
  } catch {
    return undefined
  }
}

export function localeInfo(locale: SiteLocale) {
  return SITE_LOCALES.find((entry) => entry.code === locale)!
}

export function formatLocale(locale: SiteLocale): string {
  return locale === 'zh-Hant' ? 'zh-HK' : locale
}
